// Lead magnet email-capture popup. Appears 10s after page load, then re-appears
// after a random 5-10 day interval each time it's dismissed or submitted.
import { useEffect, useRef, useState } from "react";
import { X, Loader2 } from "lucide-react";
import api from "@/lib/api";

const NAVY = "#1B2F5E";
const CREAM = "#F5F0E8";
const GOLD = "#E8952A";
const OVERLAY = "rgba(27, 47, 94, 0.7)";
const POPUP_TIMESTAMP_KEY = "leadMagnetLastDismissed";
const POPUP_INTERVAL_KEY = "leadMagnetDismissInterval";
const MIN_DAYS = 5;
const MAX_DAYS = 10;
const SHOW_AFTER_MS = 10_000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Events used to coordinate with StickyLeadBar.
export const LEAD_MAGNET_OPEN_EVENT = "leadmagnet:open";
export const LEAD_MAGNET_CLOSE_EVENT = "leadmagnet:close";
export const LEAD_MAGNET_FORCE_OPEN_EVENT = "leadmagnet:force-open";

function getRandomInterval() {
  const days = Math.floor(Math.random() * (MAX_DAYS - MIN_DAYS + 1)) + MIN_DAYS;
  return days * 24 * 60 * 60 * 1000;
}

function recordDismissal() {
  try {
    localStorage.setItem(POPUP_TIMESTAMP_KEY, Date.now().toString());
    localStorage.setItem(POPUP_INTERVAL_KEY, getRandomInterval().toString());
  } catch { /* SSR / privacy mode — silently no-op */ }
}

export default function LeadMagnetPopup() {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const shownOnceRef = useRef(false);

  useEffect(() => {
    // Show only if never dismissed, or the random 5-10 day interval has elapsed.
    let shouldShow = true;
    try {
      const lastDismissed = localStorage.getItem(POPUP_TIMESTAMP_KEY);
      const dismissInterval = localStorage.getItem(POPUP_INTERVAL_KEY);
      shouldShow =
        !lastDismissed ||
        !dismissInterval ||
        Date.now() - parseInt(lastDismissed, 10) > parseInt(dismissInterval, 10);
    } catch { /* SSR / privacy mode — silently no-op */ }
    if (!shouldShow) return;
    const t = setTimeout(() => {
      if (shownOnceRef.current) return; // already opened (e.g. via sticky bar) this session
      shownOnceRef.current = true;
      setVisible(true);
    }, SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  // Sticky bar CTA opens the popup immediately, regardless of the random-day timer.
  useEffect(() => {
    const onForceOpen = () => {
      shownOnceRef.current = true;
      setFading(false);
      setSuccess(false);
      setError("");
      setVisible(true);
    };
    window.addEventListener(LEAD_MAGNET_FORCE_OPEN_EVENT, onForceOpen);
    return () => window.removeEventListener(LEAD_MAGNET_FORCE_OPEN_EVENT, onForceOpen);
  }, []);

  useEffect(() => {
    if (!visible) return;
    window.dispatchEvent(new CustomEvent(LEAD_MAGNET_OPEN_EVENT));
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.dispatchEvent(new CustomEvent(LEAD_MAGNET_CLOSE_EVENT));
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const close = () => {
    recordDismissal();
    setFading(true);
    setTimeout(() => setVisible(false), 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/newsletter/lead-magnet", { email: trimmed, source: "lead_magnet_popup" });
      recordDismissal();
      setSuccess(true);
      setTimeout(() => {
        setFading(true);
        setTimeout(() => setVisible(false), 250);
      }, 3000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setError(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-magnet-title"
      onClick={close}
      style={{
        position: "fixed", inset: 0, background: OVERLAY, zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem", opacity: fading ? 0 : 1, transition: "opacity 250ms ease",
      }}
      data-testid="lead-magnet-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative", width: "100%", maxWidth: 450, background: CREAM,
          border: `2px solid ${NAVY}`, borderRadius: 16, padding: "2.25rem 1.75rem 1.75rem",
          textAlign: "center", boxShadow: "0 30px 80px -20px rgba(27,47,94,0.55)",
          fontFamily: "Georgia, ui-serif, serif",
        }}
        data-testid="lead-magnet-card"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          style={{
            position: "absolute", top: 12, right: 12, width: 34, height: 34,
            border: "none", background: "transparent", color: NAVY, cursor: "pointer",
            borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}
          data-testid="lead-magnet-close"
        >
          <X size={18} />
        </button>

        {success ? (
          <div data-testid="lead-magnet-success" style={{ padding: "1rem 0" }}>
            <div style={{ fontSize: 36 }}>🎉</div>
            <h2 style={{ color: NAVY, fontSize: 24, fontWeight: 700, margin: "12px 0 6px" }}>
              Check your email!
            </h2>
            <p style={{ color: "#4A5568", fontSize: 15, lineHeight: 1.6, margin: 0 }}>
              Your free guide is on its way.
            </p>
            <a
              href="https://summarymaps.com/?utm_source=journal&utm_medium=lead_magnet&utm_campaign=beat_information_overload"
              target="_blank"
              rel="noreferrer"
              data-testid="lead-magnet-trial-cta"
              style={{
                display: "inline-block", background: NAVY, color: "#ffffff", textDecoration: "none",
                fontWeight: 700, fontSize: 14, padding: "12px 24px", borderRadius: 999, marginTop: 16,
              }}
            >
              Start your 7-day free trial →
            </a>
            <p style={{ color: "#8B7E6A", fontSize: 12, margin: "10px 0 0" }}>
              Turn any book into a visual mind map with AI — free trial, no credit card needed.
            </p>
            <p style={{ color: "#8B7E6A", fontSize: 12, margin: "12px 0 0" }}>
              You're subscribed — one weekly digest of new essays, every Monday.
            </p>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 34, lineHeight: 1 }} aria-hidden>📚</div>
            <h2
              id="lead-magnet-title"
              style={{ color: NAVY, fontSize: 28, fontWeight: 700, lineHeight: 1.15, margin: "14px 0 10px" }}
            >
              Stop Drowning in Information
            </h2>
            <p style={{ color: "#4A5568", fontSize: 16, lineHeight: 1.6, margin: "0 0 20px", fontFamily: "system-ui, -apple-system, sans-serif" }}>
              Get our free guide: <strong style={{ color: NAVY }}>Beat Information Overload</strong> —
              learn how to cut through the noise and retain what actually matters.
            </p>

            <form onSubmit={submit}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                autoFocus
                required
                style={{
                  width: "100%", padding: "13px 16px", borderRadius: 10, border: `1px solid ${NAVY}33`,
                  background: "#ffffff", color: "#2D3748", fontSize: 15, boxSizing: "border-box",
                  fontFamily: "system-ui, -apple-system, sans-serif", outline: "none",
                }}
                data-testid="lead-magnet-email"
              />
              {error ? (
                <div style={{ color: "#B91C1C", fontSize: 13, marginTop: 8, textAlign: "left" }} data-testid="lead-magnet-error">
                  {error}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: "100%", marginTop: 12, padding: "14px 16px", border: "none",
                  background: GOLD, color: "#ffffff", fontWeight: 700, fontSize: 15,
                  borderRadius: 999, cursor: submitting ? "not-allowed" : "pointer",
                  fontFamily: "system-ui, -apple-system, sans-serif",
                  opacity: submitting ? 0.75 : 1,
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
                data-testid="lead-magnet-submit"
              >
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send Me the Free Guide"}
              </button>
            </form>

            <p style={{ color: "#5C5344", fontSize: 12, margin: "12px 0 0", fontFamily: "system-ui, -apple-system, sans-serif" }}>
              No spam. Unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
